import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Form, Table, Button, Card, FilterGrid } from '@/components/ui';
import ReportParametersCard from '../components/ReportParametersCard';
import TaskDrillModal from '../components/TaskDrillModal';
import ReportSummaryCard from '../components/ReportSummaryCard';
import { REPORT_HEADER_BG, reportHeaderCellStyle } from '../utils/reportTable';
import { areasService, ServiceArea } from '../../../services/areasService';
import { pickCurrentPlanYear, QUANTIFIED_VALUE_REPORT_SECTION } from '../utils/quantifiedValueReport';
import activityService from '../../../services/activityService';
import { Activity } from '../../../types/Activity';
import reportService, { QuantifiedValueBucket } from '../../../services/reportService';
import Toast from '../../../components/Toast';
import InfoTooltip from '../../../components/InfoTooltip';
import { MultiSelectDropdown } from '../../../components/MultiSelectDropdown';
import { monthHeaderText, PLACEHOLDER_MONTH_HEADERS } from '../utils/monthHeader';

interface QBSQuantifiedValueReportViewProps {
  serviceAreas: ServiceArea[];
  loadingServiceAreas: boolean;
  defaultServiceAreaId?: string;
}

interface MonthValueBreakdown {
  actual: number;
  planned: number;
  backlog: number;
  total: number;
}

const BUCKET_OPTIONS: { value: QuantifiedValueBucket; label: string; description: string }[] = [
  { value: 'actual', label: 'Actual', description: 'Active, marked complete, internal review, closed, finalized' },
  { value: 'planned', label: 'Planned', description: 'Planned' },
  { value: 'backlog', label: 'Backlog', description: 'Proposed' }
];
const BUCKET_ORDER = BUCKET_OPTIONS.map((o) => o.value);
const BUCKET_LABEL: Record<QuantifiedValueBucket, string> = { actual: 'Actual', planned: 'Planned', backlog: 'Backlog' };
const BUCKET_VALUE_KEY: Record<QuantifiedValueBucket, 'actualValueToDate' | 'plannedValueToDate' | 'backlogValueToDate'> = {
  actual: 'actualValueToDate',
  planned: 'plannedValueToDate',
  backlog: 'backlogValueToDate'
};
const DEFAULT_BUCKETS: QuantifiedValueBucket[] = ['actual', 'planned'];

/** Reads the bucket selection from the URL; falls back to the pre-bucket actual=/planned= params. */
function bucketsFromSearchParams(searchParams: URLSearchParams): QuantifiedValueBucket[] {
  const raw = searchParams.get('buckets');
  if (raw !== null) {
    const parts = raw.split(',');
    return BUCKET_ORDER.filter((b) => parts.includes(b));
  }
  const legacy: QuantifiedValueBucket[] = [];
  if (searchParams.get('actual') !== '0') legacy.push('actual');
  if (searchParams.get('planned') !== '0') legacy.push('planned');
  return legacy;
}

interface ActivityRow {
  activity: Activity;
  /** Every activity id merged into this row, so drill-through matches the displayed number. */
  activityIds: number[];
  unitOfMeasure: string;
  monthValues: MonthValueBreakdown[];
  actualValueToDate: number;
  plannedValueToDate: number;
  backlogValueToDate: number;
  totalValueToDate: number;
  totalAccomplishment: number;
  totalPlannedAccomplishment: number;
}

interface ReportDiagnostics {
  tasksSkippedNoDate: number;
  tasksSkippedOutsideMonths: number;
}

interface ReportSummary {
  annualTarget: number | null;
  totalActualValue: number;
  totalPlannedValue: number;
  totalBacklogValue: number;
  totalValue: number;
  projectedPercentOfTarget: number | null;
}

interface DrillThroughTask {
  taskId: number;
  taskNumber: string | null;
  taskName: string | null;
  status: string;
  value: number;
  accomplishment: number | null;
  date: string | null;
}

const QBSQuantifiedValueReportView: React.FC<QBSQuantifiedValueReportViewProps> = ({
  serviceAreas,
  loadingServiceAreas,
  defaultServiceAreaId
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Filters of the last run, restored from the URL when the user comes Back from a task page.
  // The report is re-run automatically once the restored year's activities have loaded.
  const restoredRef = useRef<{ serviceArea: string; quantifiedYear: string; autoRun: boolean } | null>(
    searchParams.get('report') === QUANTIFIED_VALUE_REPORT_SECTION && searchParams.get('sa') && searchParams.get('year')
      ? { serviceArea: searchParams.get('sa')!, quantifiedYear: searchParams.get('year')!, autoRun: true }
      : null
  );

  const [filters, setFilters] = useState(() => {
    const restored = restoredRef.current;
    return {
      serviceArea: restored?.serviceArea || defaultServiceAreaId || '',
      quantifiedYear: '',
      startDate: restored ? searchParams.get('start') || '' : '',
      endDate: restored ? searchParams.get('end') || '' : '',
      buckets: restored ? bucketsFromSearchParams(searchParams) : DEFAULT_BUCKETS
    };
  });

  // Buckets the table currently shows: those of the last run, so changing the picker doesn't
  // relabel numbers that haven't been re-fetched yet.
  const [shownBuckets, setShownBuckets] = useState<QuantifiedValueBucket[]>(filters.buckets);

  const [qbsPlanYearOptions, setQbsPlanYearOptions] = useState<number[]>([]);
  const [loadingQbsPlanYears, setLoadingQbsPlanYears] = useState(false);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [activityRows, setActivityRows] = useState<ActivityRow[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false);
  const [monthHeaders, setMonthHeaders] = useState<Array<{ label: string; dateRange: string }>>([]);
  const [diagnostics, setDiagnostics] = useState<ReportDiagnostics | null>(null);
  const [summary, setSummary] = useState<ReportSummary | null>(null);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [showToast, setShowToast] = useState(false);

  const [drillThrough, setDrillThrough] = useState<{
    show: boolean;
    activityId: number | null;
    activityLabel: string;
    monthLabel: string;
    unitOfMeasure: string;
    monthIndex: number | null;
    tasks: DrillThroughTask[];
    loading: boolean;
    error: string | null;
  }>({
    show: false,
    activityId: null,
    activityLabel: '',
    monthLabel: '',
    unitOfMeasure: '',
    monthIndex: null,
    tasks: [],
    loading: false,
    error: null
  });

  // `${serviceArea}-${year}` the activity list was last loaded for; gates the restored auto-run.
  const [activitiesLoadedFor, setActivitiesLoadedFor] = useState<string | null>(null);

  useEffect(() => {
    if (defaultServiceAreaId && !filters.serviceArea) {
      setFilters(prev => ({ ...prev, serviceArea: defaultServiceAreaId }));
    }
  }, [defaultServiceAreaId]);

  useEffect(() => {
    if (!filters.serviceArea) {
      setQbsPlanYearOptions([]);
      setFilters((prev) => ({ ...prev, quantifiedYear: '' }));
      return;
    }
    let cancelled = false;
    const serviceAreaId = parseInt(filters.serviceArea, 10);
    if (Number.isNaN(serviceAreaId)) {
      setQbsPlanYearOptions([]);
      return;
    }
    setLoadingQbsPlanYears(true);
    areasService
      .getQuantifiedPlans(serviceAreaId)
      .then((plans) => {
        if (cancelled) return;
        const eligiblePlans = plans.filter((p) => p.status === 'Open' || p.status === 'Closed');
        const years = Array.from(new Set(eligiblePlans.map((p) => p.year))).sort((a, b) => b - a);
        setQbsPlanYearOptions(years);
        const restored = restoredRef.current;
        const restoredYear = restored && restored.serviceArea === filters.serviceArea
          ? parseInt(restored.quantifiedYear, 10)
          : NaN;
        const defaultYear = years.includes(restoredYear) ? restoredYear : pickCurrentPlanYear(eligiblePlans);
        if (restored && !years.includes(restoredYear)) restoredRef.current = null;
        setFilters((prev) => ({
          ...prev,
          quantifiedYear: defaultYear != null ? String(defaultYear) : ''
        }));
      })
      .catch(() => {
        if (!cancelled) {
          setQbsPlanYearOptions([]);
          setFilters((prev) => ({ ...prev, quantifiedYear: '' }));
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingQbsPlanYears(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters.serviceArea]);

  useEffect(() => {
    const fetchActivities = async () => {
      if (!filters.serviceArea || !filters.quantifiedYear) {
        setActivities([]);
        setActivityRows([]);
        setSummary(null);
        return;
      }

      setLoadingActivities(true);
      try {
        const filtered = await activityService.getQuantifiedActivitiesByServiceAreaAndYear(
          Number(filters.serviceArea),
          Number(filters.quantifiedYear)
        );
        setActivities(filtered);
        setActivityRows([]);
        setSummary(null);
        setActivitiesLoadedFor(`${filters.serviceArea}-${filters.quantifiedYear}`);
      } catch (error) {
        console.error('Error fetching activities:', error);
        setErrorMessage('Failed to load activities. Please try again.');
        setActivities([]);
        setActivityRows([]);
        setSummary(null);
      } finally {
        setLoadingActivities(false);
      }
    };

    fetchActivities();
  }, [filters.serviceArea, filters.quantifiedYear]);

  useEffect(() => {
    const restored = restoredRef.current;
    if (!restored?.autoRun) return;
    if (activitiesLoadedFor !== `${restored.serviceArea}-${restored.quantifiedYear}`) return;
    restoredRef.current = null;
    handleGeneratePreview();
  }, [activitiesLoadedFor]);

  const handleGeneratePreview = async () => {
    if (!filters.serviceArea || !filters.quantifiedYear) {
      setErrorMessage('Please select Service Area and Quantified Year');
      return;
    }
    if (filters.buckets.length === 0) {
      setErrorMessage('Please select at least one of Actual, Planned or Backlog under Show Values');
      return;
    }
    const runBuckets = BUCKET_ORDER.filter((b) => filters.buckets.includes(b));

    setLoadingPreview(true);
    setErrorMessage(null);
    try {
      const data = await reportService.getQBSQuantifiedValue({
        serviceAreaId: parseInt(filters.serviceArea),
        quantifiedYear: parseInt(filters.quantifiedYear),
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        buckets: runBuckets
      });

      setShownBuckets(runBuckets);
      setMonthHeaders(data.monthHeaders || []);
      setDiagnostics(data.diagnostics || null);
      setSummary(data.summary || null);

      const rows: ActivityRow[] = data.activityRows.map(row => {
        const activity = activities.find(a => a.id === row.activityId) || {
          id: row.activityId,
          activityNumber: row.activityNumber,
          activityName: row.activityName,
          unitOfMeasure: row.unitOfMeasure || ''
        } as Activity;

        return {
          activity,
          activityIds: row.activityIds?.length ? row.activityIds : [row.activityId],
          unitOfMeasure: row.unitOfMeasure || activity.unitOfMeasure || '',
          monthValues: row.monthValues,
          actualValueToDate: row.actualValueToDate,
          plannedValueToDate: row.plannedValueToDate,
          backlogValueToDate: row.backlogValueToDate || 0,
          totalValueToDate: row.totalValueToDate,
          totalAccomplishment: row.totalAccomplishment || 0,
          totalPlannedAccomplishment: row.totalPlannedAccomplishment || 0
        };
      });

      setActivityRows(rows);

      // Record the run in the URL (replace, not push) so Back from a drilled-into task restores it.
      const params = new URLSearchParams({
        report: QUANTIFIED_VALUE_REPORT_SECTION,
        sa: filters.serviceArea,
        year: filters.quantifiedYear,
        buckets: runBuckets.join(',')
      });
      if (filters.startDate) params.set('start', filters.startDate);
      if (filters.endDate) params.set('end', filters.endDate);
      setSearchParams(params, { replace: true });
    } catch (error: any) {
      console.error('Error generating QBS Quantified Value report:', error);
      setErrorMessage(error?.response?.data?.message || 'Failed to generate QBS Quantified Value report. Please try again later.');
      setActivityRows([]);
      setDiagnostics(null);
      setSummary(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleDrillThrough = async (row: ActivityRow, monthIndex: number, monthLabel: string) => {
    if (!filters.serviceArea || !filters.quantifiedYear) return;
    const activityId = row.activity.id;
    if (!activityId) return;

    const activityLabel = row.activity.activityNumber && row.activity.activityName
      ? `(#${row.activity.activityNumber}) ${row.activity.activityName}`
      : row.activity.activityName || row.activity.activityNumber || 'Unknown Activity';

    setDrillThrough({
      show: true,
      activityId,
      activityLabel,
      monthLabel,
      unitOfMeasure: row.unitOfMeasure || row.activity.unitOfMeasure || '',
      monthIndex,
      tasks: [],
      loading: true,
      error: null
    });

    try {
      const tasks = await reportService.getQBSQuantifiedValueDrillThrough({
        serviceAreaId: parseInt(filters.serviceArea),
        quantifiedYear: parseInt(filters.quantifiedYear),
        activityId,
        activityIds: row.activityIds,
        monthIndex,
        buckets: shownBuckets,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined
      });
      setDrillThrough(prev => ({ ...prev, tasks, loading: false }));
    } catch (err: any) {
      setDrillThrough(prev => ({
        ...prev,
        loading: false,
        error: err?.response?.data?.message || 'Failed to load tasks. Please try again.'
      }));
    }
  };

  const closeDrillThrough = () => {
    setDrillThrough(prev => ({ ...prev, show: false }));
  };

  const formatDollar = (value: number | string, showZero = false): string => {
    if (value === '' || value === null || value === undefined) return '-';
    const num = typeof value === 'number' ? value : parseFloat(String(value));
    if (isNaN(num)) return '-';
    if (num === 0 && !showZero) return '-';
    return '$' + Math.round(num).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  const formatQuantity = (value: number | string): string => {
    if (value === '' || value === null || value === undefined) return '-';
    const num = typeof value === 'number' ? value : parseFloat(String(value));
    if (isNaN(num) || num === 0) return '-';
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const displayedMonthValue = (cell: MonthValueBreakdown): number =>
    shownBuckets.reduce((sum, bucket) => sum + (cell[bucket] || 0), 0);

  const truncateActivityName = (text: string, maxLength: number = 50): string => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const showSuccessToast = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
  };

  const handleExport = async () => {
    if (!filters.serviceArea || !filters.quantifiedYear) {
      setErrorMessage('Please select Service Area and Quantified Year');
      return;
    }

    setLoadingExport(true);
    setErrorMessage(null);
    try {
      await reportService.downloadQBSQuantifiedValue({
        serviceAreaId: parseInt(filters.serviceArea),
        quantifiedYear: parseInt(filters.quantifiedYear),
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        buckets: shownBuckets
      });
      showSuccessToast('QBS Quantified Value report exported successfully!');
    } catch (error: any) {
      console.error('Error exporting QBS Quantified Value report:', error);
      setErrorMessage(error?.response?.data?.message || 'Failed to export QBS Quantified Value report. Please try again later.');
    } finally {
      setLoadingExport(false);
    }
  };

  const viewModeLabel = shownBuckets.map((b) => BUCKET_LABEL[b]).join(' + ');

  const renderMonthTooltip = (cell: MonthValueBreakdown) => (
    <>
      {shownBuckets.map((bucket) => (
        <React.Fragment key={bucket}>
          {BUCKET_LABEL[bucket]}: {formatDollar(cell[bucket] || 0, true)}
          <br />
        </React.Fragment>
      ))}
      Total: {formatDollar(displayedMonthValue(cell), true)}
    </>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'auto' }}>
      <div style={{ flexShrink: 0 }}>
        {errorMessage && <Alert variant="danger" className="tw-mb-3">{errorMessage}</Alert>}
        {diagnostics && diagnostics.tasksSkippedNoDate > 0 && (
          <Alert variant="warning" className="tw-mb-3 tw-py-2">
            {diagnostics.tasksSkippedNoDate} task{diagnostics.tasksSkippedNoDate === 1 ? '' : 's'} excluded: no completion or planned date.
            {diagnostics.tasksSkippedOutsideMonths > 0 && (
              <> {diagnostics.tasksSkippedOutsideMonths} task{diagnostics.tasksSkippedOutsideMonths === 1 ? '' : 's'} excluded: date outside the 12 contract months.</>
            )}
          </Alert>
        )}

        <ReportParametersCard
          onGenerate={handleGeneratePreview}
          onExport={() => {}}
          loadingGenerate={loadingPreview}
          loadingExport={false}
          disableExport={true}
          generateButtonText="Run Report"
          hideExportButton={true}
          defaultCollapsed={false}
          onCollapseChange={setIsFiltersCollapsed}
        >
          <FilterGrid>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">Service Area</Form.Label>
              <Form.Select
                size="sm"
                value={filters.serviceArea}
                onChange={(e) => setFilters(prev => ({ ...prev, serviceArea: e.target.value }))}
                disabled={loadingServiceAreas}
              >
                <option value="">All Service Areas</option>
                {serviceAreas.map((area) => (
                  <option key={area.id} value={area.id.toString()}>
                    {area.name || area.code || 'Unknown'}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">Start Date</Form.Label>
              <Form.Control
                size="sm"
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">End Date</Form.Label>
              <Form.Control
                size="sm"
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">Quantified Year</Form.Label>
              <Form.Select
                size="sm"
                value={filters.quantifiedYear}
                onChange={(e) => setFilters(prev => ({ ...prev, quantifiedYear: e.target.value }))}
                disabled={!filters.serviceArea || loadingQbsPlanYears || qbsPlanYearOptions.length === 0}
              >
                <option value="">
                  {!filters.serviceArea
                    ? 'Select service area first'
                    : loadingQbsPlanYears
                      ? 'Loading years…'
                      : qbsPlanYearOptions.length === 0
                        ? 'No quantified plans'
                        : 'Select year'}
                </option>
                {qbsPlanYearOptions.map((y) => (
                  <option key={y} value={String(y)}>{y}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">Show Values</Form.Label>
              <MultiSelectDropdown
                options={BUCKET_OPTIONS}
                value={filters.buckets}
                onChange={(selected) =>
                  setFilters(prev => ({
                    ...prev,
                    buckets: BUCKET_ORDER.filter((b) => selected.includes(b))
                  }))
                }
                formatOption={(option) => `${option.label} (${option.description})`}
                formatDisplay={(option) => option.label}
                getValue={(option) => option.value}
                placeholder="Select values"
                preserveOptionsOrder
                showSearch={false}
              />
            </Form.Group>
          </FilterGrid>
        </ReportParametersCard>

        {summary && (
          <ReportSummaryCard
            title="Annual Target Progress"
            icon="bi-graph-up-arrow"
            tooltipId="qv-summary-tooltip"
            tooltip={
              <>
                Projected % of Target = (Total Actual + Total Planned) ÷ Annual Target.
                Backlog (proposed) is not committed work, so it is left out.
                <br />
                Reflects the current Show Values selection ({viewModeLabel}).
              </>
            }
            items={[
              {
                label: 'Annual Target',
                value: summary.annualTarget !== null ? formatDollar(summary.annualTarget, true) : 'Not Set',
                muted: summary.annualTarget === null
              },
              { label: 'Total Actual', value: formatDollar(summary.totalActualValue, true) },
              { label: 'Total Planned', value: formatDollar(summary.totalPlannedValue, true) },
              ...(shownBuckets.includes('backlog')
                ? [{ label: 'Total Backlog', value: formatDollar(summary.totalBacklogValue || 0, true) }]
                : []),
              { label: `Total ${viewModeLabel}`, value: formatDollar(summary.totalValue, true) },
              // Plain text colour for now (Gord, Oct 2026): the green/amber/red bands get revisited
              // once budgeting buckets replace the "Do Not Use" buckets.
              {
                label: 'Projected % of Target',
                value: summary.projectedPercentOfTarget !== null ? `${summary.projectedPercentOfTarget.toFixed(1)}%` : '–',
                emphasis: true,
                muted: summary.projectedPercentOfTarget === null
              }
            ]}
          />
        )}
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}>
        <Card style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, minWidth: 0 }}>
          <Card.Header className="tw-flex tw-justify-between tw-items-center" style={{ flexShrink: 0 }}>
            <h6 className="tw-mb-0">
              Quantified Value – <span className="tw-text-content-muted tw-font-normal" style={{ fontSize: '0.9em' }}>{viewModeLabel}</span>
              {activities.length > 0 && (
                <span className="tw-text-content-muted tw-ml-2" style={{ fontSize: '0.875rem', fontWeight: 'normal' }}>
                  ({activities.length} {activities.length === 1 ? 'activity' : 'activities'})
                </span>
              )}
            </h6>
            <div className="tw-flex tw-items-center tw-gap-2">
              {loadingActivities && (
                <span className="tw-text-content-muted" style={{ fontSize: '0.875rem' }}>Loading activities...</span>
              )}
              {activityRows.length > 0 && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleExport}
                  disabled={!filters.serviceArea || !filters.quantifiedYear || loadingExport}
                >
                  <i className="bi bi-download tw-mr-1"></i>
                  {loadingExport ? 'Exporting...' : 'Export Report'}
                </Button>
              )}
            </div>
          </Card.Header>

          {/* No viewport-relative maxHeight here - it's what made the horizontal scrollbar land
              off-screen (see ReportPreviewTable.tsx for the same fix on the shared table). This
              box now sizes purely from flex, so its scrollbar always sits at the real edge of the
              visible table area. */}
          <Card.Body className="tw-p-0" style={{ flex: 1, minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <div style={{ overflow: 'auto', minHeight: 0, minWidth: 0, height: '100%' }}>
              {activityRows.length === 0 && !loadingActivities ? (
                <div className="tw-text-center tw-py-5 tw-text-content-muted">
                  {!filters.serviceArea || !filters.quantifiedYear
                    ? 'Please select Service Area and Quantified Year to view activities'
                    : 'No activities found for the selected filters. Run the report to load value data.'}
                </div>
              ) : (
                <Table striped bordered hover style={{ marginBottom: 0, width: 'max-content', minWidth: '100%' }}>
                  <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10 }}>
                    <tr>
                      <th style={{ maxWidth: '20%', width: '20%', backgroundColor: REPORT_HEADER_BG, position: 'sticky', left: 0, zIndex: 12, boxShadow: '2px 0 4px rgba(0,0,0,0.1)' }} title="Activity">
                        Activity
                      </th>
                      {(monthHeaders.length > 0 ? monthHeaders : PLACEHOLDER_MONTH_HEADERS).map((month, i) => (
                        <th key={month.label} style={reportHeaderCellStyle('100px')} title={monthHeaderText(month, i)}>
                          {monthHeaderText(month, i)}
                        </th>
                      ))}
                      {shownBuckets.map((bucket) => (
                        <th key={bucket} style={reportHeaderCellStyle('130px')} title={`${BUCKET_LABEL[bucket]} Value to date ($)`}>
                          {BUCKET_LABEL[bucket]} Value to date ($)
                        </th>
                      ))}
                      {shownBuckets.length > 1 && (
                        <th style={reportHeaderCellStyle('130px')} title="Total Value to date ($)">Total Value to date ($)</th>
                      )}
                      <th style={reportHeaderCellStyle('140px')} title="Total Accomplished">Total Accomplished</th>
                      <th style={reportHeaderCellStyle('80px')} title="Units">Units</th>
                      <th style={reportHeaderCellStyle('150px')} title="Total Planned Accomplish">Total Planned Accomplish</th>
                      <th style={reportHeaderCellStyle('80px')} title="Units">Units</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activityRows.map((row, index) => (
                      <tr key={row.activity.id || index}>
                        <td style={{ padding: '8px 12px', position: 'sticky', left: 0, zIndex: 9, backgroundColor: 'white', boxShadow: '2px 0 4px rgba(0,0,0,0.1)', maxWidth: '20%', width: '20%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {truncateActivityName(
                            row.activity.activityNumber && row.activity.activityName
                              ? `(#${row.activity.activityNumber}) ${row.activity.activityName}`
                              : row.activity.activityName || row.activity.activityNumber || 'Unknown Activity'
                          )}
                        </td>
                        {row.monthValues.map((cell, monthIndex) => {
                          const value = displayedMonthValue(cell);
                          const hasValue = value !== 0;
                          const label = monthHeaderText(monthHeaders[monthIndex], monthIndex);
                          return (
                            <td
                              key={monthIndex}
                              style={{
                                textAlign: 'right',
                                padding: '8px 12px',
                                cursor: hasValue ? 'pointer' : 'default',
                                color: hasValue ? '#0d6efd' : undefined,
                                textDecoration: hasValue ? 'underline' : undefined
                              }}
                              title={hasValue ? `Click to see tasks for ${label}` : undefined}
                              onClick={hasValue ? () => handleDrillThrough(row, monthIndex, label) : undefined}
                            >
                              <InfoTooltip
                                content={renderMonthTooltip(cell)}
                                id={`qv-tooltip-${row.activity.id}-${monthIndex}`}
                                placement="top"
                              >
                                {formatDollar(value)}
                              </InfoTooltip>
                            </td>
                          );
                        })}
                        {shownBuckets.map((bucket) => (
                          <td key={bucket} style={{ textAlign: 'right', padding: '8px 12px' }}>{formatDollar(row[BUCKET_VALUE_KEY[bucket]])}</td>
                        ))}
                        {shownBuckets.length > 1 && (
                          <td style={{ textAlign: 'right', padding: '8px 12px' }}>{formatDollar(row.totalValueToDate)}</td>
                        )}
                        <td style={{ textAlign: 'right', padding: '8px 12px' }}>{formatQuantity(row.totalAccomplishment)}</td>
                        <td style={{ textAlign: 'center', padding: '8px 12px' }}>{row.unitOfMeasure || row.activity.unitOfMeasure || '-'}</td>
                        <td style={{ textAlign: 'right', padding: '8px 12px' }}>{formatQuantity(row.totalPlannedAccomplishment)}</td>
                        <td style={{ textAlign: 'center', padding: '8px 12px' }}>{row.unitOfMeasure || row.activity.unitOfMeasure || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          </Card.Body>
        </Card>
      </div>

      <TaskDrillModal
        show={drillThrough.show}
        onHide={closeDrillThrough}
        title={<>Tasks – {drillThrough.activityLabel}</>}
        // monthLabel is already the calendar month (W-M14), so no separate date range.
        subtitle={<>{drillThrough.monthLabel} · {viewModeLabel}</>}
        tasks={drillThrough.tasks}
        loading={drillThrough.loading}
        error={drillThrough.error}
        quantityUnit={drillThrough.unitOfMeasure}
      />

      <Toast message={toastMessage} type="success" show={showToast} onClose={() => setShowToast(false)} />
    </div>
  );
};

export default QBSQuantifiedValueReportView;
